/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import { Capacitor } from "@capacitor/core";

// Surfaces the platform's notification permission prompt at a natural moment.
// Invoked from the onboarding NotificationPrimerScreen (right after Welcome)
// and EulaScreen.handleAccept; safe to call anywhere else that wants the
// prompt (Walk Me Home setup, SOS first-use, etc.).
//
// iOS push delivery IS wired: the APNs Auth Key is configured in the Firebase
// Console, the aps-environment entitlement is set (dev + prod), and the FCM
// token captured by useFCMTokenCapture lands at /users/{uid}/fcmToken, where
// the safeCircleNotifications / sendHauntNudge cloud functions deliver visible
// push (SOS, SafeCircle alerts, messages). Granting here primes that path.
// (Supersedes the earlier Build-90 "not yet wired" caveat.)

export interface NotificationPermissionResult {
  /** Whether the user already had a decision before this call ran. */
  alreadyDecided: boolean;
  /** Final permission state after the call. */
  state: "granted" | "denied" | "prompt" | "unsupported";
}

export type NotificationPermissionState =
  | "granted"
  | "denied"
  | "prompt"
  | "unsupported";

/**
 * Read the current notification permission WITHOUT prompting. Used to decide
 * whether to show the onboarding primer ("Stay in the loop"): once the user
 * has GRANTED, the primer is skipped forever; while still denied/undecided it
 * keeps appearing on every login so they get another chance to turn it on.
 * Never throws.
 */
export async function getNotificationPermissionState(): Promise<NotificationPermissionState> {
  const platform = Capacitor.getPlatform();
  if (platform === "ios" || platform === "android") {
    try {
      const { PushNotifications } = await import("@capacitor/push-notifications");
      const status = await PushNotifications.checkPermissions();
      if (status.receive === "granted") return "granted";
      if (status.receive === "denied") return "denied";
      return "prompt";
    } catch {
      return "unsupported";
    }
  }
  if (typeof Notification === "undefined") return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  return "prompt";
}

/**
 * Best-effort: show the platform's notification permission prompt if the
 * user hasn't decided yet. Resolves once the dialog closes (or immediately
 * if no prompt was needed). Never throws — all platform-shape mismatches
 * are swallowed so a permission flow can never block account creation.
 */
export async function requestNotificationPermission(): Promise<NotificationPermissionResult> {
  const platform = Capacitor.getPlatform();

  // Native iOS / Android — use @capacitor/push-notifications. The plugin's
  // requestPermissions() drives the iOS-native "Would Like to Send You
  // Notifications" alert; on Android 13+ it drives POST_NOTIFICATIONS.
  if (platform === "ios" || platform === "android") {
    try {
      const { PushNotifications } = await import("@capacitor/push-notifications");
      const status = await PushNotifications.checkPermissions();
      // The plugin reports `prompt` or `prompt-with-rationale` on the
      // first call (and `denied` / `granted` after the user decides).
      if (status.receive === "granted") {
        return { alreadyDecided: true, state: "granted" };
      }
      if (status.receive === "denied") {
        return { alreadyDecided: true, state: "denied" };
      }
      const next = await PushNotifications.requestPermissions();
      return {
        alreadyDecided: false,
        state: next.receive === "granted" ? "granted" : "denied",
      };
    } catch (err) {
      console.warn("[notificationPermission] native request failed:", err);
      return { alreadyDecided: false, state: "unsupported" };
    }
  }

  // Web — use the standard Notification API. iOS WKWebView technically
  // supports this too, but the experience is the web-style banner, not a
  // proper iOS push prompt; the native branch above is preferred.
  if (typeof Notification === "undefined") {
    return { alreadyDecided: true, state: "unsupported" };
  }
  if (Notification.permission === "granted") {
    return { alreadyDecided: true, state: "granted" };
  }
  if (Notification.permission === "denied") {
    return { alreadyDecided: true, state: "denied" };
  }
  try {
    const result = await Notification.requestPermission();
    return {
      alreadyDecided: false,
      state: result === "granted" ? "granted" : "denied",
    };
  } catch (err) {
    console.warn("[notificationPermission] web request failed:", err);
    return { alreadyDecided: false, state: "unsupported" };
  }
}
